import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Pagination, Row, Spinner, Table } from 'react-bootstrap';
import { FiArrowLeft, FiRefreshCw, FiInbox } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { itAssetsAPI } from '../utils/api';
import { getUser } from '../utils/auth';

const formatDate = (value) => {
  if (!value) return '-';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) return value.trim();
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const iso = date.toISOString();
  // If there's a time component, show it for auditability.
  return iso.includes('T') ? iso.replace('T', ' ').slice(0, 19) : iso.slice(0, 10);
};

const stringifyCell = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const parsePayloadJson = (value) => {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(String(value));
  } catch {
    return { _raw: String(value) };
  }
};

const toTitleLabel = (key) => {
  const raw = String(key || '').trim();
  if (!raw) return '';

  const normalized = raw
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();

  const upperWords = new Set(['id', 'ip', 'os', 'cpu', 'ram', 'sap', 'dt']);
  return normalized
    .split(' ')
    .map((w) => {
      const lower = w.toLowerCase();
      if (upperWords.has(lower)) return lower.toUpperCase();
      if (lower === 'gb') return 'GB';
      return lower ? lower[0].toUpperCase() + lower.slice(1) : '';
    })
    .join(' ');
};

const ITAssetApprovalsPage = () => {
  const navigate = useNavigate();
  const roleType = String(getUser()?.roleType || getUser()?.role?.roleType || '').toUpperCase();
  const canApprove = roleType === 'ADMIN' || roleType === 'IT_HEAD';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [editForm, setEditForm] = useState({});

  const load = useCallback(async (status = statusFilter) => {
    if (!canApprove) return;
    setLoading(true);
    setError('');
    try {
      const normalizedStatus = String(status || '').trim().toUpperCase();
      const res = await itAssetsAPI.listRequests({ status: normalizedStatus || undefined });
      const data = Array.isArray(res?.data) ? res.data : res?.data?.data || [];
      const filtered = normalizedStatus
        ? data.filter((row) => String(row?.status || '').toUpperCase() === normalizedStatus)
        : data;
      setRows(filtered);
      setPage(1);
      setSelectedIds(new Set());
    } catch (e) {
      setError(e?.message || 'Failed to load approval requests.');
    } finally {
      setLoading(false);
    }
  }, [canApprove, statusFilter]);

  useEffect(() => {
    // Load once on mount with the default filter (Pending). Every other reload
    // (filter button clicks, approve/reject, bulk actions) calls load()
    // directly, so this must NOT re-run just because `load` changed identity
    // (it does, on every statusFilter change) — that was overwriting the
    // selected filter with an unfiltered "All" list.
    load('PENDING');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const payloadKeys = useMemo(() => {
    const keys = new Set();
    for (const row of (rows || [])) {
      const payload = parsePayloadJson(row?.payload_json);
      if (!payload || typeof payload !== 'object') continue;
      for (const k of Object.keys(payload)) keys.add(k);
    }
    // Hide noisy/internal fields in approval UI (they're not needed for IT Head decisions).
    const hidden = new Set([
      'action',
      'userId',
      'allocatedUserId',
      'allocUserId',
      // No longer used
      'allocReasonForDeactivation',
      'allocDeactivationRemarks',
    ]);

    const list = Array.from(keys.values())
      .filter((k) => !hidden.has(String(k)))
      .map((k) => ({ key: k, label: toTitleLabel(k) || String(k) }));

    // Preferred column ordering for IT approvals.
    const afterWarrantyStartPrimary = [
      'serialNumber',
      'status',
      'userName',
      'email',
      'empId',
      'employeeType',
      'hostName',
      'ipAddress',
      'lastDeallocationDate',
      'location',
      'manufacture',
      'modelDetails',
      'osDetails'
    ];

    const afterWarrantyStart = [
      'allocDeactivationDate',
      'allocEmail',
      'allocEmpId',
      'allocLastDeallocationDate',
      'allocUserName'
    ];

    const preferredOrder = [
      // Keep warranty dates together and then show allocation-specific deactivation/meta fields.
      'warrantyStartDate',
      ...afterWarrantyStartPrimary,
      ...afterWarrantyStart,
      'warrantyEndDate'
    ];

    const rank = new Map(preferredOrder.map((k, idx) => [k, idx]));

    return list.sort((a, b) => {
      const ra = rank.has(a.key) ? rank.get(a.key) : Number.POSITIVE_INFINITY;
      const rb = rank.has(b.key) ? rank.get(b.key) : Number.POSITIVE_INFINITY;
      if (ra !== rb) return ra - rb;
      return a.label.localeCompare(b.label);
    });
  }, [rows]);

  const totalPages = Math.max(1, Math.ceil((rows || []).length / pageSize));
  const pageSafe = Math.min(Math.max(page, 1), totalPages);
  const startIndex = (pageSafe - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, (rows || []).length);
  const rowsPage = (rows || []).slice(startIndex, startIndex + pageSize);
  const pendingRowsPage = useMemo(
    () => rowsPage.filter((row) => String(row?.status || '').toUpperCase() === 'PENDING'),
    [rowsPage]
  );

  const toggleSelected = (id, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(Number(id));
      else next.delete(Number(id));
      return next;
    });
  };

  const toggleSelectAllPendingOnPage = (checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const row of pendingRowsPage) {
        const id = Number(row?.id);
        if (!Number.isFinite(id)) continue;
        if (checked) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  };

  const allPendingSelectedOnPage =
    pendingRowsPage.length > 0
    && pendingRowsPage.every((row) => selectedIds.has(Number(row?.id)));

  const selectedCount = selectedIds.size;

  const approve = async (id) => {
    setError('');
    setSuccess('');
    try {
      await itAssetsAPI.approveRequest(id);
      setSuccess('Request approved.');
      await load(statusFilter);
    } catch (e) {
      setError(e?.message || 'Failed to approve request.');
    }
  };

  const reject = async (id) => {
    const reason = window.prompt('Rejection reason (optional):', '');
    setError('');
    setSuccess('');
    try {
      await itAssetsAPI.rejectRequest(id, { reason: reason || '' });
      setSuccess('Request rejected.');
      await load(statusFilter);
    } catch (e) {
      setError(e?.message || 'Failed to reject request.');
    }
  };

  const openEdit = (row) => {
    setEditingRow(row);
    setEditForm({ ...parsePayloadJson(row?.payload_json) });
    setError('');
    setSuccess('');
    setShowEditModal(true);
  };

  const saveEdit = async () => {
    if (!editingRow) return;
    setError('');
    setSuccess('');
    try {
      await itAssetsAPI.updateRequestPayload(editingRow.id, { payload: editForm });
      setSuccess(`Request #${editingRow.id} updated — you can now approve it.`);
      setShowEditModal(false);
      setEditingRow(null);
      await load(statusFilter);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to update request.');
    }
  };

  const bulkApprove = async () => {
    if (!selectedCount) return;
    setError('');
    setSuccess('');
    try {
      const ids = Array.from(selectedIds.values());
      for (const id of ids) {
        const row = rows.find((r) => Number(r.id) === Number(id));
        if (String(row?.status || '').toUpperCase() !== 'PENDING') continue;
        // eslint-disable-next-line no-await-in-loop
        await itAssetsAPI.approveRequest(id);
      }
      setSuccess(`Approved ${ids.length} request(s).`);
      await load(statusFilter);
    } catch (e) {
      setError(e?.message || 'Bulk approve failed.');
    }
  };

  const bulkReject = async () => {
    if (!selectedCount) return;
    const reason = window.prompt('Rejection reason (optional):', '');
    setError('');
    setSuccess('');
    try {
      const ids = Array.from(selectedIds.values());
      for (const id of ids) {
        const row = rows.find((r) => Number(r.id) === Number(id));
        if (String(row?.status || '').toUpperCase() !== 'PENDING') continue;
        // eslint-disable-next-line no-await-in-loop
        await itAssetsAPI.rejectRequest(id, { reason: reason || '' });
      }
      setSuccess(`Rejected ${ids.length} request(s).`);
      await load(statusFilter);
    } catch (e) {
      setError(e?.message || 'Bulk reject failed.');
    }
  };

  if (!canApprove) {
    return (
      <Container fluid className="py-4">
        <Alert variant="danger">Access denied.</Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Row className="align-items-center mb-3">
        <Col>
          <div className="d-flex align-items-center gap-2">
            <Button
              variant="outline-secondary"
              onClick={() => {
                // Approvals is a deep route; returning to the main dashboard route keeps the
                // "Select dashboard" switching working without requiring a hard refresh.
                navigate('/dashboard');
              }}
            >
              <FiArrowLeft className="me-1" /> Back
            </Button>
            <div>
              <h1 className="page-title" style={{ fontSize: 'var(--font-size-3xl)' }}>IT Asset Approvals</h1>
              <p className="page-subtitle mb-0">Review and approve/reject workflow requests.</p>
            </div>
          </div>
        </Col>
        <Col className="text-end">
          <Button variant="outline-secondary" onClick={() => load(statusFilter)} disabled={loading}>
            <FiRefreshCw className="me-1" /> Refresh
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      <Card className="border-0 shadow-sm card-accent-top">
      <Card.Body>
      <Row className="g-2 align-items-end mb-2">
        <Col md="auto">
          <Form.Label className="d-block">Status</Form.Label>
          <div className="btn-group" role="group" aria-label="Status filter">
            {[
              { value: 'PENDING', label: 'Pending' },
              { value: 'APPROVED', label: 'Approved' },
              { value: 'REJECTED', label: 'Rejected' },
              { value: '', label: 'All' },
            ].map(({ value, label }) => (
              <Button
                key={value || 'ALL'}
                variant={statusFilter === value ? 'primary' : 'outline-primary'}
                onClick={async () => {
                  setStatusFilter(value);
                  await load(value);
                }}
              >
                {label}
              </Button>
            ))}
          </div>
        </Col>
        <Col className="text-end">
          <div className="d-flex justify-content-end gap-2">
            <Button
              variant="success"
              disabled={!selectedCount}
              onClick={bulkApprove}
            >
              Approve Selected{selectedCount ? ` (${selectedCount})` : ''}
            </Button>
            <Button
              variant="outline-danger"
              disabled={!selectedCount}
              onClick={bulkReject}
            >
              Reject Selected{selectedCount ? ` (${selectedCount})` : ''}
            </Button>
          </div>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" /></div>
      ) : (
        <>
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
            <div className="text-muted small">
              Showing {rows.length ? startIndex + 1 : 0}-{endIndex} of {rows.length}{' '}
              {rows.length ? <Badge bg="secondary">{rows.length}</Badge> : null}
            </div>
            <Form.Select
              size="sm"
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value) || 10); setPage(1); }}
              style={{ width: 120 }}
              aria-label="Approvals per page"
            >
              <option value={5}>5 / page</option>
              <option value={10}>10 / page</option>
              <option value={20}>20 / page</option>
              <option value={50}>50 / page</option>
            </Form.Select>
          </div>

          <div className="table-responsive">
            <Table bordered hover size="sm" className="mb-2">
              <thead>
                <tr>
                  <th className="text-nowrap">
                    <Form.Check
                      type="checkbox"
                      aria-label="Select all pending on page"
                      checked={allPendingSelectedOnPage}
                      disabled={!pendingRowsPage.length}
                      onChange={(e) => toggleSelectAllPendingOnPage(e.target.checked)}
                    />
                  </th>
                  <th>ID</th>
                  <th>Type</th>
	                  <th className="text-nowrap">Serial Number</th>
                  <th>Requested By</th>
                  <th className="text-nowrap">Requested At</th>
                  <th>Status</th>
                  <th>Approved By</th>
                  <th className="text-nowrap">Approved At</th>
                  <th className="text-nowrap">Rejection Reason</th>
                  {payloadKeys.map(({ key, label }) => (
                    <th key={key} className="text-nowrap">{label}</th>
                  ))}
                  <th className="text-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rowsPage.map((row) => {
                  const payload = parsePayloadJson(row?.payload_json);
                  const isPending = String(row?.status || '').toUpperCase() === 'PENDING';
                  const rowId = Number(row?.id);
                  const isChecked = selectedIds.has(rowId);
                  return (
                    <tr key={row.id}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          aria-label={`Select request ${row.id}`}
                          checked={isChecked}
                          disabled={!isPending}
                          onChange={(e) => toggleSelected(rowId, e.target.checked)}
                        />
                      </td>
                      <td>{row.id}</td>
                      <td><Badge bg="light" text="dark" className="border">{row.request_type}</Badge></td>
	                      <td>{row.serial_number || '-'}</td>
                      <td>{row.requested_by_email || row.requested_by}</td>
                      <td>{formatDate(row.requested_at)}</td>
                      <td>
                        <Badge bg={isPending ? 'warning' : (String(row.status).toUpperCase() === 'APPROVED' ? 'success' : 'danger')} text={isPending ? 'dark' : undefined}>
                          {String(row.status || '').toUpperCase()}
                        </Badge>
                      </td>
                      <td>{row.approved_by_email || row.approved_by || '-'}</td>
                      <td>{formatDate(row.approved_at)}</td>
                      <td style={{ maxWidth: 220 }} className="text-truncate" title={row.rejection_reason || ''}>
                        {row.rejection_reason || '—'}
                      </td>
                      {payloadKeys.map(({ key }) => (
                        <td key={key} className="text-nowrap">
                          {typeof payload?.[key] === 'object' ? JSON.stringify(payload?.[key]) : stringifyCell(payload?.[key])}
                        </td>
                      ))}
                      <td className="text-nowrap">
                        {String(row.status) === 'PENDING' ? (
                          <div className="d-flex gap-2">
                            <Button size="sm" variant="success" onClick={() => approve(row.id)}>Approve</Button>
                            <Button size="sm" variant="outline-secondary" onClick={() => openEdit(row)}>Edit</Button>
                            <Button size="sm" variant="outline-danger" onClick={() => reject(row.id)}>Reject</Button>
                          </div>
                        ) : (
                          <span className="text-muted small">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!rowsPage.length && (
                  <tr>
                    <td colSpan={11 + payloadKeys.length}>
                      <div className="empty-state py-4">
                        <div className="icon-chip icon-chip-sm icon-chip-secondary">
                          <FiInbox />
                        </div>
                        <div className="empty-state-title">No requests found</div>
                        <p className="empty-state-subtitle mb-0">Try a different status filter, or check back later.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {totalPages > 1 && (
            <Pagination className="justify-content-center mb-0">
              <Pagination.First onClick={() => setPage(1)} disabled={pageSafe === 1} />
              <Pagination.Prev onClick={() => setPage(pageSafe - 1)} disabled={pageSafe === 1} />
              <Pagination.Item active>{pageSafe}</Pagination.Item>
              <Pagination.Next onClick={() => setPage(pageSafe + 1)} disabled={pageSafe === totalPages} />
              <Pagination.Last onClick={() => setPage(totalPages)} disabled={pageSafe === totalPages} />
            </Pagination>
          )}
        </>
      )}
      </Card.Body>
      </Card>

      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Request #{editingRow?.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-muted small mb-3">
            {editingRow?.request_type} — Serial Number: <span className="fw-semibold">{editingRow?.serial_number || '-'}</span>
          </div>
          {Object.keys(editForm || {}).length ? Object.keys(editForm).map((key) => {
            const label = toTitleLabel(key) || key;
            const isDateField = /date/i.test(key);
            const rawValue = editForm[key];
            const value = typeof rawValue === 'object' && rawValue !== null ? JSON.stringify(rawValue) : (rawValue ?? '');
            return (
              <Form.Group className="mb-3" key={key}>
                <Form.Label>{label}</Form.Label>
                <Form.Control
                  type={isDateField ? 'date' : 'text'}
                  value={value}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, [key]: e.target.value }))}
                />
              </Form.Group>
            );
          }) : (
            <div className="text-muted small">This request has no editable fields.</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={saveEdit}>Save Changes</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ITAssetApprovalsPage;
